/**
 * Painel de detalhe de estoque (Gerenciamento / Estoques).
 * Totais e validade são só apresentação — não altera regra de negócio.
 */

const DASH = '—';

/**
 * @param {unknown} value
 * @returns {number}
 */
export function parseStockQty(value)
{
	if (value == null || value === '')
		return 0;
	const s = String(value).trim().replace(/\s/g, '');
	if (!s)
		return 0;
	// BR com milhar + decimal: 5.983,50
	if (s.includes(',') && s.includes('.'))
		return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
	// Só vírgula decimal: 5,98
	if (s.includes(','))
		return Number(s.replace(',', '.')) || 0;
	// Só ponto: milhar BR (5.983 / 1.234.567) — NÃO tratar como decimal US
	// (Number("5.983") === 5.983 gerava KPI "5,98" / total "15,98").
	if (/^\d{1,3}(\.\d{3})+$/.test(s))
		return Number(s.replace(/\./g, '')) || 0;
	return Number(s) || 0;
}

/**
 * Estoque total da célula = disponível + reservado + bloqueado.
 * @param {number} available
 * @param {number} reserved
 * @param {number} blocked
 * @returns {number}
 */
export function stockCellTotal(available, reserved, blocked)
{
	return parseStockQty(available) + parseStockQty(reserved) + parseStockQty(blocked);
}

/**
 * Saldo livre da célula = quantidade disponível (status Livre / saldo operacional).
 * @param {number} available
 * @returns {number}
 */
export function stockCellFree(available)
{
	return parseStockQty(available);
}

/**
 * Quantidade completa (sem abreviação). Inteiros com milhar pt-BR: 5993 → "5.993".
 * @param {number} qty
 * @returns {string}
 */
export function formatStockQty(qty)
{
	const n = parseStockQty(qty);
	if (!Number.isFinite(n))
		return '0';
	if (Number.isInteger(n))
		return n.toLocaleString('pt-BR');
	return n.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function dashText(value)
{
	const s = value == null ? '' : String(value).trim();
	if (!s || s === '-' || s === '—')
		return DASH;
	return s;
}

/**
 * @param {unknown} value
 * @returns {Date|null}
 */
export function parseLooseDate(value)
{
	const s = value == null ? '' : String(value).trim();
	if (!s)
		return null;
	let y = 0;
	let m = 0;
	let d = 0;
	const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso)
	{
		y = Number(iso[1]);
		m = Number(iso[2]);
		d = Number(iso[3]);
	}
	else
	{
		const br = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
		if (!br)
			return null;
		d = Number(br[1]);
		m = Number(br[2]);
		y = Number(br[3]);
	}
	const dt = new Date(y, m - 1, d);
	if (Number.isNaN(dt.getTime()) || dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d)
		return null;
	return dt;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatDateBr(value)
{
	const dt = parseLooseDate(value);
	if (!dt)
		return dashText(value);
	const dd = String(dt.getDate()).padStart(2, '0');
	const mm = String(dt.getMonth() + 1).padStart(2, '0');
	return `${dd}/${mm}/${dt.getFullYear()}`;
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function daysUntilDate(value)
{
	const dt = parseLooseDate(value);
	if (!dt)
		return null;
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	dt.setHours(0, 0, 0, 0);
	return Math.round((dt.getTime() - today.getTime()) / 86400000);
}

/**
 * @param {number|null} days
 * @returns {'expired'|'danger'|'warn'|'ok'|null}
 */
export function expiryTone(days)
{
	if (days == null)
		return null;
	if (days < 0)
		return 'expired';
	if (days <= 30)
		return 'danger';
	if (days <= 90)
		return 'warn';
	return 'ok';
}

/**
 * @param {number|null} days
 * @returns {string}
 */
export function expiryLabel(days)
{
	if (days == null)
		return '';
	if (days < 0)
		return 'vencido';
	if (days === 0)
		return 'vence hoje';
	if (days === 1)
		return '1 dia';
	return `${days} dias`;
}

/**
 * @param {unknown} status
 * @returns {'free'|'blocked'|'reserved'|'other'}
 */
export function statusTone(status)
{
	const s = String(status ?? '').trim().toLowerCase();
	if (s === '0' || s === 'livre')
		return 'free';
	if (s === '10' || s === 'bloqueado')
		return 'blocked';
	if (s === '20' || s === 'reservado')
		return 'reserved';
	return 'other';
}

/**
 * Rótulo do status a partir do código cadastrado (0/10).
 * @param {unknown} rawStatus
 * @returns {string}
 */
export function stockStatusLabel(rawStatus)
{
	const s = String(rawStatus ?? '').trim();
	if (!s || s === '-' || s === '—')
		return DASH;
	if (s === '0')
		return 'Livre';
	if (s === '10')
		return 'Bloqueado';
	if (s === '20')
		return 'Reservado';
	return s;
}

/**
 * Status exibido no detalhe: se o saldo da célula está 100% bloqueado ou 100%
 * reservado, o badge acompanha as quantidades (não o flag cadastrado).
 * @param {number} available
 * @param {number} reserved
 * @param {number} blocked
 * @param {unknown} rawStatus
 * @returns {{ label: string, tone: 'free'|'blocked'|'reserved'|'other' }}
 */
export function effectiveStockStatus(available, reserved, blocked, rawStatus)
{
	const a = parseStockQty(available);
	const r = parseStockQty(reserved);
	const b = parseStockQty(blocked);
	const total = a + r + b;
	if (total > 0 && a === 0)
	{
		if (b === total)
			return { label: 'Bloqueado', tone: 'blocked' };
		if (r === total)
			return { label: 'Reservado', tone: 'reserved' };
	}
	return { label: stockStatusLabel(rawStatus), tone: statusTone(rawStatus) };
}

/**
 * Copia valores dos inputs (formset) para o painel visível.
 * @param {HTMLElement|null} root
 */
export function syncStockDetailPanel(root)
{
	if (!root)
		return;
	const val = (sel) =>
	{
		const el = root.querySelector(sel);
		return el ? String(el.value ?? '').trim() : '';
	};

	const setText = (sel, text) =>
	{
		root.querySelectorAll(sel).forEach((el) =>
		{
			el.textContent = text;
		});
	};

	const product = dashText(val('#stock_product_name'));
	setText('[data-sdp="product"]', product === DASH ? 'Produto sem descrição' : product);
	setText('[data-sdp="sku"]', dashText(val('#stock_sku')));
	setText('[data-sdp="lot-number"]', dashText(val('#stock_lot_number')));
	setText('[data-sdp="manufacturer"]', dashText(val('#stock_manufacturer_nick')));

	const avail = parseStockQty(val('#quantity_available'));
	const reserved = parseStockQty(val('#quantity_reserved'));
	const blocked = parseStockQty(val('#quantity_unsettled'));
	const { label: statusLabel, tone } = effectiveStockStatus(avail, reserved, blocked, val('#status'));
	root.querySelectorAll('[data-sdp="status"]').forEach((el) =>
	{
		el.textContent = statusLabel;
		el.classList.remove('sdp-badge--free', 'sdp-badge--blocked', 'sdp-badge--reserved', 'sdp-badge--other');
		el.classList.add(`sdp-badge--${tone}`);
	});

	const total = stockCellTotal(avail, reserved, blocked);
	const free = stockCellFree(avail);
	setText('[data-sdp="kpi-total"]', formatStockQty(total));
	setText('[data-sdp="kpi-avail"]', formatStockQty(avail));
	setText('[data-sdp="kpi-reserved"]', formatStockQty(reserved));
	setText('[data-sdp="kpi-blocked"]', formatStockQty(blocked));
	setText('[data-sdp="kpi-free"]', formatStockQty(free));

	root.querySelectorAll('[data-sdp-from]').forEach((el) =>
	{
		const from = el.getAttribute('data-sdp-from');
		const kind = el.getAttribute('data-sdp-kind') || 'text';
		const raw = val(from);
		if (kind === 'date')
			el.textContent = raw ? formatDateBr(raw) : DASH;
		else if (kind === 'qty')
			el.textContent = `${formatStockQty(raw)} un`;
		else
			el.textContent = dashText(raw);
	});

	const days = daysUntilDate(val('#stock_expiration_date'));
	const expEl = root.querySelector('[data-sdp="expiry"]');
	if (expEl)
	{
		const t = expiryTone(days);
		const label = expiryLabel(days);
		expEl.hidden = !label;
		expEl.textContent = label;
		expEl.classList.remove('sdp-expiry--expired', 'sdp-expiry--danger', 'sdp-expiry--warn', 'sdp-expiry--ok');
		if (t)
			expEl.classList.add(`sdp-expiry--${t}`);
	}
}
